// src/pages/Homepage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Button,
  Divider,
  Paper,
  Tooltip,
  Link,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  InputAdornment,
  Avatar
} from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StockCharts from '../components/StockCharts';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ChatIcon from '@mui/icons-material/Chat';
import CancelIcon from '@mui/icons-material/Cancel';
import InputArea from '../components/InputArea';
import {
  CenteredContainer,
  TrendingSection,
  StockCardContainer,
  StockChip,
  ChatContainer,
  MessageBubble
} from '../styles/StyledComponents';

import Sidebar from '../components/Sidebar';
import { getAIResponse, analyzeStock } from '../services/api';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { formatDate, extractSections, extractPrice, extractPercentage } from '../utils/formatting';

// Enhanced Typing Animation with Formatted Text
const TypingFormattedText = ({ text, speed = 2, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (!text) return;
    
    let index = 0;
    setDisplayedText('');
    setIsTyping(true);
    
    const timer = setInterval(() => {
      if (index < text.length) {
        // Add 8-12 characters at once for very fast effect
        const charsToAdd = Math.min(10, text.length - index);
        setDisplayedText(prev => prev + text.slice(index, index + charsToAdd));
        index += charsToAdd;
      } else {
        setIsTyping(false);
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, onComplete]);

  // Blinking cursor effect
  useEffect(() => {
    if (!isTyping) {
      const cursorTimer = setInterval(() => {
        setShowCursor(prev => !prev);
      }, 500);
      
      return () => clearInterval(cursorTimer);
    } else {
      setShowCursor(true);
    }
  }, [isTyping]);

  return (
    <Box>
      <FormattedText text={displayedText} />
      {(isTyping || showCursor) && (
        <Box 
          component="span"
          sx={{ 
            borderRight: '2px solid #00D2FF',
            animation: isTyping ? 'none' : 'blink 1s infinite',
            marginLeft: '2px',
            '@keyframes blink': {
              '0%, 50%': { opacity: 1 },
              '51%, 100%': { opacity: 0 }
            }
          }}
        >
          &nbsp;
        </Box>
      )}
    </Box>
  );
};

// Enhanced Text Renderer with Markdown Support
const FormattedText = ({ text }) => {
  const theme = useTheme();
  
  const formatText = (text) => {
    if (!text) return '';
    
    // Split text into lines for processing
    const lines = text.split('\n');
    const formattedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines but preserve spacing
      if (!line) {
        formattedLines.push(<br key={i} />);
        continue;
      }
      
      // Main heading (# )
      if (line.startsWith('# ')) {
        formattedLines.push(
          <Typography 
            key={i} 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              mb: 3, 
              mt: 2,
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              pb: 1
            }}
          >
            {line.substring(2)}
          </Typography>
        );
      }
      // Section heading (## )
      else if (line.startsWith('## ')) {
        formattedLines.push(
          <Typography 
            key={i} 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 2, 
              mt: 3,
              color: theme.palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {line.substring(3)}
          </Typography>
        );
      }
      // Bullet points
      else if (line.startsWith('- ')) {
        const bulletText = line.substring(2);
        // Check for bold formatting
        const boldRegex = /\*\*(.*?)\*\*/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = boldRegex.exec(bulletText)) !== null) {
          if (match.index > lastIndex) {
            parts.push(bulletText.substring(lastIndex, match.index));
          }
          parts.push(
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {match[1]}
            </Box>
          );
          lastIndex = match.index + match[0].length;
        }
        
        if (lastIndex < bulletText.length) {
          parts.push(bulletText.substring(lastIndex));
        }
        
        formattedLines.push(
          <Box key={i} sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
            <Box sx={{ 
              minWidth: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: theme.palette.primary.main, 
              mt: 1, 
              mr: 2 
            }} />
            <Typography sx={{ lineHeight: 1.6, color: theme.palette.text.secondary }}>
              {parts.length > 0 ? parts : bulletText}
            </Typography>
          </Box>
        );
      }
      // Regular paragraphs
      else {
        // Process bold text
        const boldRegex = /\*\*(.*?)\*\*/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = boldRegex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            parts.push(line.substring(lastIndex, match.index));
          }
          parts.push(
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {match[1]}
            </Box>
          );
          lastIndex = match.index + match[0].length;
        }
        
        if (lastIndex < line.length) {
          parts.push(line.substring(lastIndex));
        }
        
        formattedLines.push(
          <Typography 
            key={i} 
            sx={{ 
              mb: 2, 
              lineHeight: 1.7,
              color: theme.palette.text.secondary,
              fontSize: '1rem'
            }}
          >
            {parts.length > 0 ? parts : line}
          </Typography>
        );
      }
    }
    
    return formattedLines;
  };
  
  return (
    <Box sx={{ 
      '& > *:first-of-type': { mt: 0 },
      '& > *:last-child': { mb: 0 }
    }}>
      {formatText(text)}
    </Box>
  );
};

// Prominent investors and analysts list
const prominentInvestors = [
  // Active Individual Investors / Analysts
  { 
    name: 'Warren Buffett', 
    title: 'CEO of Berkshire Hathaway',
    query: 'What are Warren Buffett\'s latest portfolio changes and holdings at Berkshire Hathaway in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Cathie Wood', 
    title: 'CEO of ARK Invest',
    query: 'What are Cathie Wood\'s latest ARK Invest portfolio changes and trades in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Ray Dalio', 
    title: 'Founder of Bridgewater Associates',
    query: 'What are Ray Dalio\'s latest investment insights and Bridgewater Associates portfolio changes in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Bill Ackman', 
    title: 'CEO of Pershing Square Capital',
    query: 'What are Bill Ackman\'s latest Pershing Square Capital portfolio changes and investment moves in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Michael Burry', 
    title: 'Founder of Scion Asset Management',
    query: 'What are Michael Burry\'s latest Scion Asset Management portfolio changes and market predictions in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Jim Cramer', 
    title: 'CNBC Mad Money Host',
    query: 'What are Jim Cramer\'s latest stock recommendations and market commentary from Mad Money in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Joel Greenblatt', 
    title: 'Managing Principal at Gotham Asset Management',
    query: 'What are Joel Greenblatt\'s latest portfolio moves and value investing insights at Gotham Asset Management in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Stanley Druckenmiller', 
    title: 'Active Private Investor',
    query: 'What are Stanley Druckenmiller\'s latest investment moves and market commentary in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'David Einhorn', 
    title: 'President of Greenlight Capital',
    query: 'What are David Einhorn\'s latest Greenlight Capital portfolio changes and investment strategy in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  { 
    name: 'Chamath Palihapitiya', 
    title: 'CEO of Social Capital',
    query: 'What are Chamath Palihapitiya\'s latest Social Capital investments and SPAC activities in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Individual'
  },
  // Personal Analysts and Financial Personalities
  { 
    name: 'Kevin O\'Leary', 
    title: 'Shark Tank Investor & O\'Leary Ventures',
    query: 'What are Kevin O\'Leary\'s latest investment recommendations and market analysis from Shark Tank and O\'Leary Ventures in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Suze Orman', 
    title: 'Personal Finance Expert & Author',
    query: 'What are Suze Orman\'s latest financial advice and market insights for individual investors in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Charlie Munger', 
    title: 'Vice Chairman of Berkshire Hathaway',
    query: 'What are Charlie Munger\'s latest investment philosophy insights and Berkshire Hathaway commentary in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Carl Icahn', 
    title: 'Activist Investor & Icahn Enterprises',
    query: 'What are Carl Icahn\'s latest activist investment moves and portfolio changes at Icahn Enterprises in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Paul Tudor Jones', 
    title: 'Hedge Fund Manager & Tudor Investment',
    query: 'What are Paul Tudor Jones\'s latest market predictions and trading strategies at Tudor Investment in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Peter Thiel', 
    title: 'PayPal Co-founder & Thiel Capital',
    query: 'What are Peter Thiel\'s latest venture capital investments and tech sector insights at Thiel Capital in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'George Soros', 
    title: 'Soros Fund Management',
    query: 'What are George Soros\'s latest macroeconomic predictions and Soros Fund Management portfolio moves in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Maria Bartiromo', 
    title: 'Fox Business Host & Financial Journalist',
    query: 'What are Maria Bartiromo\'s latest market analysis and financial interviews on Fox Business in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Tom Lee', 
    title: 'Fundstrat Global Advisors Co-founder',
    query: 'What are Tom Lee\'s latest market forecasts and technical analysis from Fundstrat Global Advisors in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  },
  { 
    name: 'Kathy Lien', 
    title: 'BK Asset Management Managing Director',
    query: 'What are Kathy Lien\'s latest forex and currency market analysis from BK Asset Management in the last week?',
    icon: <PersonIcon fontSize="small" />,
    category: 'Analyst'
  }
];

export default function Homepage({ toggleMode }) {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    messages,
    addMessage,
    activeId,
    sessions,
    isLoadingSessions,
    isOperating,
    newSession,
    setSessions,
    setActiveId,
    setMessages,
    setIsLoadingSessions
  } = useChat();

  const { currentUser, bypassAuth } = useAuth();

  const [isGenerating, setIsGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [expandedCharts, setExpandedCharts] = useState({});
  const [stockAnalysisMode, setStockAnalysisMode] = useState(false);
  const [stockTicker, setStockTicker] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [typingMessageId, setTypingMessageId] = useState(null);
  const chatEndRef = useRef(null);
  const scrollBoxRef = useRef(null);

  // Auto scroll to bottom when new messages come in
  useEffect(() => {
    if (chatEndRef.current) {
      const scrollBox = scrollBoxRef.current;
      if (scrollBox) {
        const isNearBottom = scrollBox.scrollHeight - scrollBox.scrollTop - scrollBox.clientHeight < 100;
        if (isNearBottom) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages]);

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
  };

  // Function to handle sending messages
  const handleSendMessage = async (text, forceStockAnalysis = false, bypassDetection = false) => {
    if (!text.trim()) return;

    // Determine if this should be stock analysis
    let isStockAnalysisRequest = forceStockAnalysis;
    
    // If not forced and not bypassing detection, let AI decide based on context and content
    if (!forceStockAnalysis && !bypassDetection) {
      // Simple heuristics to detect stock analysis intent
      const stockKeywords = /\b(stock|ticker|share|equity|invest|analysis|price|earnings|dividend|market cap|P\/E|financial|portfolio|buy|sell|hold)\b/i;
      const hasTickerPattern = /\b[A-Z]{1,5}\b/.test(text);
      const isCompanyQuery = /\b(company|corporation|inc|corp|ltd|llc)\b/i.test(text);
      
      isStockAnalysisRequest = stockKeywords.test(text) || hasTickerPattern || isCompanyQuery;
    }
    
    // If bypassing detection, ensure it's NOT stock analysis
    if (bypassDetection) {
      isStockAnalysisRequest = false;
    }
    
    const isTickerSearch = stockAnalysisMode && 
                           /^[A-Za-z]{1,5}$/.test(text.trim()) && 
                           !text.includes('?');
    
    if (!activeId) {
      try {
        setSnackbar({
          open: true,
          message: 'Creating a new chat session...',
          severity: 'info'
        });
        
        const newSessionId = await newSession();
        if (!newSessionId) {
          setSnackbar({
            open: true,
            message: 'Failed to create a new chat session',
            severity: 'error'
          });
          return;
        }
      } catch (error) {
        console.error('Error creating new session:', error);
        setSnackbar({
          open: true,
          message: `Failed to create a new chat session: ${error.message}`,
          severity: 'error'
        });
        return;
      }
    }

    const userMessage = {
      sender: 'user',
      text,
      timestamp: Timestamp.now(),
    };
    
    try {
      await addMessage(userMessage);
    } catch (error) {
      console.error('Error saving user message:', error);
      setSnackbar({
        open: true,
        message: `Failed to save your message: ${error.message}`,
        severity: 'error'
      });
      return;
    }

    setIsGenerating(true);
    const timeoutId = setTimeout(() => {
      if (isGenerating) {
        setIsGenerating(false);
        setSnackbar({
          open: true,
          message: 'Request timed out. Please try again.',
          severity: 'error'
        });
      }
    }, 30000);

    try {
      console.log('Fetching AI response for:', text.substring(0, 50) + (text.length > 50 ? '...' : ''));
      console.log('Stock analysis mode:', isStockAnalysisRequest);
      
      if (isStockAnalysisRequest) {
        setSnackbar({
          open: true,
          message: `Analyzing "${text}" with latest market data...`,
          severity: 'info'
        });
      }
      
      const response = isStockAnalysisRequest 
        ? await analyzeStock(text)
        : await getAIResponse(text);
      
      clearTimeout(timeoutId);
      
      if (isStockAnalysisRequest) {
        setSnackbar({
          open: true,
          message: `Analysis of "${text}" complete!`,
          severity: 'success'
        });
      }
      
      if (response.structuredData) {
        setAnalysisResult(response);
        setStockAnalysisMode(true);
        setStockTicker(text.toUpperCase());
      }
      
      const responseText = typeof response.text === 'string' 
        ? response.text 
        : JSON.stringify(response.text) || 'No response text available';
      
      const aiMessage = {
        sender: 'ai',
        text: responseText,
        timestamp: Timestamp.now(),
        structuredData: response.structuredData
      };
      
      const messageId = await addMessage(aiMessage);
      
      // Trigger typing animation for the new message
      if (messageId) {
        setTypingMessageId(messageId);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      
      console.error("Error fetching AI response:", err);
      
      let errorMessage = err.message || 'Error processing your request';
      let errorDetails = '';
      
      if (errorMessage.includes('API generation failed')) {
        errorMessage = 'The AI service encountered an error generating the response.';
        errorDetails = 'This might be due to server load or API limitations. Please try again in a moment.';
      } else if (errorMessage.includes('timed out')) {
        errorMessage = 'The request timed out.';
        errorDetails = 'The server took too long to respond. This might be due to high traffic or complex analysis. Please try a simpler query or try again later.';
      } else if (errorMessage.includes('Network')) {
        errorMessage = 'Network connection error.';
        errorDetails = 'Please check your internet connection and try again.';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      const errorMessageObj = {
        sender: 'ai',
        text: `I encountered an error: ${errorMessage}${errorDetails ? '\n\n' + errorDetails : ''}\n\nPlease try again or rephrase your question.`,
        timestamp: Timestamp.now(),
        error: true
      };
      
      try {
        await addMessage(errorMessageObj);
      } catch (addError) {
        console.error('Failed to add error message:', addError);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle direct stock analysis (always triggers stock analysis mode)
  const handleStockAnalysis = async () => {
    if (!stockTicker.trim()) return;
    
    const query = stockTicker.trim();
    
    setStockAnalysisMode(true);
    
    try {
      const analysisStartTime = new Date();
      // Always use stock analysis mode for top input field
      await handleSendMessage(query, true); // Force stock analysis mode
      const timeSinceAnalysis = (new Date() - analysisStartTime) / 1000;
      console.log(`Analysis completed in ${timeSinceAnalysis.toFixed(2)} seconds`);
      
      setSnackbar({
        open: true,
        message: `Please signin first to search for a stock`,
        severity: 'success'
      });
      
    } catch (error) {
      console.error("Stock analysis error:", error);
      setSnackbar({
        open: true,
        message: `Error analyzing "${query}": ${error.message}`,
        severity: 'error'
      });
      
      setStockAnalysisMode(false);
    }
  };

  const handleInvestorClick = (query) => {
    // Use bypassDetection=true to ensure investor queries are NOT treated as stock analysis
    handleSendMessage(query, false, true);
  };

  const handleCopyMessage = (text) => {
    const textToCopy = typeof text === 'string' ? text : JSON.stringify(text);
    navigator.clipboard.writeText(textToCopy);
    setSnackbar({
      open: true,
      message: 'Message copied to clipboard',
      severity: 'success'
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleChartExpansion = (messageId) => {
    setExpandedCharts(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Filter investors based on selected category
  const filteredInvestors = selectedCategory === 'All' 
    ? prominentInvestors 
    : prominentInvestors.filter(investor => investor.category === selectedCategory);

  const renderStockAnalysisUI = () => {
    const messageText = messages[messages.length - 1]?.text || '';
    const timestamp = messages[messages.length - 1]?.timestamp;
    const formattedAnalysisTime = formatDate(timestamp, true);
    
    // Extract key information from the analysis
    const currentPrice = messageText.match(/\$([0-9,.]+)/)?.[1];
    const changePercent = messageText.match(/([+-]?[0-9.]+%)/)?.[1];
    const recommendation = messageText.match(/\*\*RECOMMENDATION\*\*:\s*(Strong Buy|Buy|Hold|Sell|Strong Sell)/i)?.[1];
    
    const recommendationColor = 
      recommendation?.includes('Buy') ? theme.palette.success.main :
      recommendation?.includes('Sell') ? theme.palette.error.main :
      theme.palette.warning.main;
    
    return (
      <Box sx={{ 
        width: '100%', 
        height: '100%',
        overflowY: 'auto',
        p: 0,
        backgroundColor: theme.palette.background.default,
      }}>        
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3,
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: '1px solid rgba(148, 163, 184, 0.1)',
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" component="h1" sx={{ 
                fontWeight: 700, 
                color: theme.palette.text.primary,
                background: 'linear-gradient(135deg, #00D2FF 0%, #0094CC 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {stockTicker.toUpperCase()}
              </Typography>
              {recommendation && (
                <Chip 
                  label={recommendation} 
                  sx={{ 
                    bgcolor: recommendationColor,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    px: 1,
                  }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              {currentPrice && (
                <Typography variant="h5" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.text.primary 
                }}>
                  ${currentPrice}
                </Typography>
              )}
              {changePercent && (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: changePercent.includes('-') ? theme.palette.error.main : theme.palette.success.main,
                    fontWeight: 600,
                  }}
                >
                  {changePercent}
                </Typography>
              )}
            </Box>
          </Box>  
          
          <Button 
            startIcon={<ChatIcon />} 
            variant="outlined" 
            onClick={() => setStockAnalysisMode(false)}
            sx={{ 
              color: theme.palette.text.primary, 
              borderColor: theme.palette.divider,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: 'rgba(0, 210, 255, 0.05)'
              }
            }}
          >
            Back to Chat
          </Button>
        </Box>
        
        {/* Content */}
        {isGenerating ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 400,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: '1px solid rgba(148, 163, 184, 0.1)',
          }}>
            <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ mt: 3, color: theme.palette.text.primary, fontWeight: 500 }}>
              Analyzing {stockTicker.toUpperCase()}...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              Gathering real-time market data and insights
            </Typography>
          </Box>
        ) : (
          <Paper sx={{ 
            p: 4, 
            backgroundColor: theme.palette.background.paper,
            border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 2,
          }}>
            <TypingFormattedText 
              text={messageText} 
              speed={2}
              onComplete={() => setTypingMessageId(null)}
            />
          </Paper>
        )}
      </Box>
    );
  };

  if (isLoadingSessions) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 3,
        backgroundColor: theme.palette.background.default
      }}>
        <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
          Loading your financial insights...
        </Typography>
        
        <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {currentUser ? `Logged in as ${currentUser.email}` : 'Not authenticated'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Require authentication for new users
  if (!currentUser && !bypassAuth) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 3,
        backgroundColor: theme.palette.background.default,
        px: 4
      }}>
        <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mb: 2,
            color: theme.palette.primary.main
          }}>
            Welcome to Cricket AI
          </Typography>
          
          <Typography variant="h6" sx={{ 
            mb: 3, 
            color: theme.palette.text.secondary,
            lineHeight: 1.6
          }}>
            Track portfolio changes from prominent investors and get real-time market insights.
          </Typography>
          
          <Typography variant="body1" sx={{ 
            mb: 4, 
            color: theme.palette.text.primary,
            fontWeight: 500
          }}>
            Please sign in to access your personalized financial dashboard.
          </Typography>
          
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              minWidth: 200,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
            onClick={() => {
              // You can trigger sign-in modal or redirect here
              window.location.reload(); // Simple reload to trigger auth flow
            }}
          >
            Sign In to Continue
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh',
      backgroundColor: theme.palette.background.default,
    }}>
      <Sidebar toggleMode={toggleMode} />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Main Content Area */}
        <Box sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0, // Important for proper flex behavior
        }}>
          {messages.length === 0 && !isGenerating && !isOperating && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '1400px',
                mx: 'auto',
                width: '100%',
                textAlign: 'center',
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 4 },
              }}
            >
              {/* Hero Section */}
              <Box sx={{ mb: 4, maxWidth: 800 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{ 
                    fontWeight: 800, 
                    mb: 2,
                    background: 'linear-gradient(135deg, #00D2FF 0%, #0094CC 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => handleNavigation('/')}
                >
                  Cricket AI
                </Typography>
                
                <Typography
                  variant="h6"
                  sx={{ 
                    mb: 3, 
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    fontSize: { xs: '0.95rem', md: '1.1rem' }
                  }}
                >
                  Track portfolio changes from the world's most prominent investors and analysts. 
                  Get real-time insights on their latest moves and market strategies.
                </Typography>
                
                {/* Stock Analysis Input */}
                <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter Stock Ticker (AAPL) or Company Name (Apple Inc)"
                    value={stockTicker}
                    onChange={(e) => setStockTicker(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button 
                            variant="contained" 
                            onClick={handleStockAnalysis}
                            sx={{ 
                              minWidth: 100,
                              fontWeight: 600,
                            }}
                          >
                            Analyze
                          </Button>
                        </InputAdornment>
                      ),
                      sx: { 
                        py: 1,
                        fontSize: '1.1rem',
                        '& input': {
                          color: theme.palette.text.primary,
                          fontSize: '1.1rem',
                        },
                        '& input::placeholder': {
                          color: theme.palette.text.secondary,
                          opacity: 0.8,
                        }
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && stockTicker.trim()) {
                        handleStockAnalysis();
                      }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Investors Section */}
              <Box sx={{ width: '100%', maxWidth: 1400 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 4, 
                    fontWeight: 700, 
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '1.75rem', md: '2.125rem' }
                  }}
                >
                  📈 Prominent Investors & Analysts
                </Typography>
                
                {/* Category Filter */}
                <Box sx={{ 
                        display: 'flex',
                  gap: 2, 
                  mb: 3, 
                  flexWrap: 'wrap', 
                  justifyContent: 'center' 
                }}>
                  {['All', 'Individual', 'Analyst'].map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      onClick={() => setSelectedCategory(category)}
                      variant={selectedCategory === category ? 'filled' : 'outlined'}
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: selectedCategory === category 
                          ? theme.palette.primary.contrastText 
                          : theme.palette.primary.main,
                        backgroundColor: selectedCategory === category 
                          ? theme.palette.primary.main 
                          : 'transparent',
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: selectedCategory === category 
                            ? theme.palette.primary.dark 
                            : 'rgba(0, 210, 255, 0.1)',
                          transform: 'translateY(-1px)',
                        }
                      }}
                    />
                  ))}
                </Box>
                
                {/* Investors Grid */}
                <Grid container spacing={2}>
                  {filteredInvestors.map((investor, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={i}>
                      <Card 
                        onClick={() => handleInvestorClick(investor.query)}
                        sx={{
                          cursor: 'pointer',
                          height: '100%',
                          minHeight: 140,
                          maxHeight: 160,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px) scale(1.01)',
                            boxShadow: `0 12px 28px rgba(0, 210, 255, 0.15)`,
                            '& .investor-avatar': {
                              transform: 'scale(1.05)',
                            },
                            '& .investor-name': {
                              color: theme.palette.primary.main,
                            }
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Avatar 
                              className="investor-avatar"
                              sx={{ 
                                bgcolor: investor.category === 'Individual' 
                                  ? theme.palette.success.main 
                                  : theme.palette.warning.main,
                                width: 36, 
                                height: 36,
                                mr: 1.5,
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1rem'
                                }
                              }}
                            >
                              {investor.icon}
                            </Avatar>
                            <Chip 
                              size="small" 
                              label={investor.category}
                              sx={{
                                bgcolor: investor.category === 'Individual' 
                                  ? theme.palette.success.main 
                                  : theme.palette.warning.main,
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                height: 20,
                                px: 0.5,
                              }}
                            />
                          </Box>
                          
                          <Typography 
                            variant="h6" 
                            className="investor-name"
                            sx={{ 
                              fontWeight: 700, 
                              mb: 0.5,
                              color: theme.palette.text.primary,
                              transition: 'color 0.3s ease',
                              lineHeight: 1.2,
                              fontSize: '1rem',
                            }}
                          >
                            {investor.name}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              lineHeight: 1.3,
                              flexGrow: 1,
                              fontSize: '0.8rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {investor.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
          
          {/* Chat Section (when active) */}
          {activeId && (
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: 0,
              px: { xs: 2, md: 4 },
              py: 2,
            }}>
              {stockAnalysisMode && renderStockAnalysisUI()}
              
              {!stockAnalysisMode && (
                <ChatContainer 
                  ref={scrollBoxRef}
                  sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.default,
                    mx: 'auto',
                    maxWidth: 800,
                    width: '100%',
                  }}
                >
                  {messages.map((message, i) => {
                    const isAI = message.sender === 'ai';
                    const messageId = message.id || message.timestamp?.toMillis() || i;
                    const uniqueKey = `message-${messageId}-${i}`;
                    const messageText = typeof message.text === 'string' ? message.text : '';
                    const showStock = isAI && messageText.includes('AAPL') && !expandedCharts[messageId];
                    
                    return (
                      <Box key={uniqueKey} sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 1,
                          color: theme.palette.text.secondary,
                          fontSize: '0.8rem'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {isAI ? 'Cricket AI' : 'You'}
                          </Typography>
                          <Typography sx={{ ml: 1, fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                        </Box>
                        
                        <MessageBubble 
                          sender={message.sender}
                          sx={{
                            p: 3,
                            position: 'relative',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            color: isAI ? theme.palette.text.secondary : theme.palette.text.primary,
                            background: isAI ? theme.palette.background.paper : theme.palette.primary.main,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderRadius: 2,
                            border: isAI ? '1px solid rgba(148, 163, 184, 0.1)' : 'none',
                            ...(message.error && {
                              borderLeft: '4px solid',
                              borderColor: theme.palette.error.main,
                            }),
                          }}
                        >
                          {isAI ? (
                            <TypingFormattedText 
                              text={messageText} 
                              speed={2}
                              onComplete={() => setTypingMessageId(null)}
                            />
                          ) : (
                            messageText
                          )}
                          
                          {isAI && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mt: 2,
                              pt: 1,
                              borderTop: '1px solid',
                              borderColor: theme.palette.divider,
                            }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="This was helpful">
                                  <IconButton size="small" sx={{ color: theme.palette.text.secondary, opacity: 0.6 }}>
                                    <ThumbUpAltOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="This was not helpful">
                                  <IconButton size="small" sx={{ color: theme.palette.text.secondary, opacity: 0.6 }}>
                                    <ThumbDownAltOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              
                              <Tooltip title="Copy to clipboard">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleCopyMessage(messageText)}
                                  sx={{ color: theme.palette.text.secondary, opacity: 0.6 }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </MessageBubble>
                        
                        {showStock && (
                          <Box 
                            sx={{ 
                              mt: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: theme.palette.background.default,
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: theme.palette.divider
                            }}
                            onClick={() => toggleChartExpansion(messageId)}
                          >
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'  
                            }}>
                              <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                AAPL Stock Performance
                              </Typography>
                              <Button size="small" sx={{ color: theme.palette.primary.main }}>View chart</Button>
                            </Box>
                          </Box>
                        )}
                        
                        {expandedCharts[messageId] && (
                          <Paper
                            elevation={2}
                            sx={{ 
                              mt: 1, 
                              p: 2, 
                              height: 300, 
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: theme.palette.background.default,
                              border: '1px solid',
                              borderColor: theme.palette.divider
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1  
                            }}>
                              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>AAPL Performance</Typography>
                              <Button 
                                size="small"
                                onClick={() => toggleChartExpansion(messageId)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                Hide
                              </Button>
                            </Box>
                            <Box sx={{ width: '100%', height: 250 }}>
                              {expandedCharts[messageId] && <StockCharts symbol="AAPL" />}
                            </Box>
                          </Paper>
                        )}
                      </Box>
                    );
                  })}
                  
                  {(isGenerating || isOperating) && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      my: 2,
                      ml: 2,
                      color: theme.palette.text.secondary
                    }}>
                      <CircularProgress size={16} thickness={5} sx={{ mr: 2, color: theme.palette.primary.main }} />
                      <Typography variant="body2">
                        {isGenerating ? 'Cricket is thinking...' : 'Processing...'}
                      </Typography>
                    </Box>
                  )}
                  
                  <div ref={chatEndRef} />
                </ChatContainer>
              )}
            </Box>
          )}
          
          {/* Empty state when no chat is selected */}
          {!activeId && !isLoadingSessions && messages.length > 0 && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
              px: 4,
            }}>
              <Typography variant="body1" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                Start a new chat or select an existing one.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => {
                  // Start a new session or navigate to home
                  if (newSession) {
                    newSession();
                  } else {
                    handleNavigation('/');
                  }
                }}
                startIcon={<ShowChartIcon />}
              >
                New financial chat
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Input Area (when chat is active) */}
        {activeId && (
          <Box sx={{ 
            p: { xs: 2, md: 3 },
            backgroundColor: theme.palette.background.default,
            borderTop: '1px solid rgba(148, 163, 184, 0.1)',
          }}>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              <InputArea
                onSendMessage={handleSendMessage}
                isGenerating={isGenerating || isOperating}
                placeholder={stockAnalysisMode ? 
                  "Ask follow-up questions about this stock..." : 
                  "Ask about any investor's portfolio changes or market insights…"}
              />
            </Box>
          </Box>
        )}
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}